import { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { deliveryPreferenceSchema } from '@/types/delivery-preference';
import { MainScreen } from '@/components/shared/MainScreen/MainScreen';
import { Button } from '@/components/ui';
import {
  useProjectView,
  useProjectBrief,
  useProjectParticipants,
} from '@/hooks/useProjectViews';
import { useProjectCommand } from '@/hooks/useProjectCommand';
import { useSkillsAndRoles } from '@/hooks/useSkillsAndRoles';
import { useAuthStore } from '@/stores/authStore';
import {
  useProposalPresentation,
  useProposalCommentWriter,
} from '@/hooks/useProposalReviews';
import { getProjectView } from '@/api/adapter/projectViews';
import {
  milestonePresentationSchema,
  putProposalPresentation,
  type MilestonePresentation,
} from '@/api/adapter/proposalReviews';
import {
  projectViewSchema,
  type ProjectView,
  type ProposalView,
} from '@/types/current-project';
import {
  proposalDefinitionSchema,
  validateProjectCommand,
  type ProjectCommand,
} from '@/types/project-command';
import { ProjectDetailsCard } from './components/BuildScope/ProjectDetailsCard';
import { MilestonesFormCard } from './components/BuildScope/MilestonesFormCard';
import { ReviewPanelCard } from './components/BuildScope/ReviewPanelCard';
import { PlanningTaskFields } from './components/BuildScope/PlanningTaskFields';
import {
  currentDetails,
  definitionOf,
  plannedMilestoneBudget,
  newMilestone,
  minimumDeliveryDate,
  type PlanningMilestone,
} from './components/BuildScope/planning-draft';
import './BuildScopePage.css';

const localMilestone =
  projectViewSchema.shape.proposals.element.shape.milestones.element.shape
    .definition;
const draftSchema = z.object({
  definition: z.object({
    title: z.string(),
    description: z.string(),
    milestones: z.array(
      localMilestone.extend({
        coordinatorFee: z.string(),
        requirements: z.array(
          localMilestone.shape.requirements.element.extend({
            budget: z.string(),
            roleId: z.number().int().min(0),
          })
        ),
      })
    ),
  }),
  details: z.array(
    milestonePresentationSchema.extend({ description: z.string() })
  ),
  delivery: z
    .object({ preference: z.string(), date: z.string().optional() })
    .default({ preference: 'WithinOneMonth' }),
  comment: z.string(),
  proposalId: z.string().optional(),
});

function moveDraft(
  oldKey: string,
  nextKey: string,
  draft: z.infer<typeof draftSchema>
) {
  sessionStorage.setItem(nextKey, JSON.stringify(draft));
  const keys = Array.from({ length: sessionStorage.length }, (_, index) =>
    sessionStorage.key(index)
  ).filter(
    (storageKey): storageKey is string =>
      !!storageKey && storageKey.startsWith(oldKey + ':')
  );
  for (const storageKey of keys)
    sessionStorage.setItem(
      nextKey + storageKey.slice(oldKey.length),
      sessionStorage.getItem(storageKey)!
    );
  sessionStorage.removeItem(oldKey);
  for (const storageKey of keys) sessionStorage.removeItem(storageKey);
}

export default function BuildScopePage() {
  const { id } = useParams<{ id: string }>();
  const query = useProjectView(id);
  const principal = useAuthStore((s) => s.session?.principalId);
  const account = useAuthStore((s) => s.session?.accountId);
  if (query.isPending)
    return (
      <p role="status" className="p-6">
        Loading proposal…
      </p>
    );
  if (query.isError)
    return (
      <div className="p-6">
        <p role="alert">{query.error.message}</p>
        <Button onClick={() => void query.refetch()}>Retry project</Button>
      </div>
    );
  const project = query.data;
  if (
    account !== project.coordinator ||
    !principal ||
    project.cancelled ||
    project.completed ||
    project.planning.frozen ||
    project.activeDisputeId ||
    !['Accepted', 'Delivered', 'Completed'].includes(project.planning.status) ||
    project.proposals.some((p) => p.status === 'Approved')
  )
    return (
      <div className="space-y-4 p-6 text-foreground">
        <p>
          Only the assigned coordinator can draft or revise the plan before
          execution approval.
        </p>
        <Link className="text-primary" to={'/projects/' + project.projectId}>
          Back to project
        </Link>
      </div>
    );
  return (
    <ProposalBuilder key={principal + project.projectId} project={project} />
  );
}
function ProposalBuilder({ project }: { project: ProjectView }) {
  const command = useProjectCommand(project.projectId);
  const saved = command.saved?.command;
  const [selected, setSelected] = useState(
    saved && 'proposalId' in saved
      ? saved.proposalId
      : saved?.kind === 'proposal-create'
        ? ''
        : (project.proposals.find((p) => p.status === 'Draft')?.proposalId ??
          '')
  );
  const proposal = project.proposals.find((p) => p.proposalId === selected);
  const brief = useProjectBrief(project.projectId);
  const presentation = useProposalPresentation(
    project.projectId,
    proposal?.proposalId
  );
  if (brief.isPending)
    return (
      <p role="status" className="p-6">
        Loading project brief…
      </p>
    );
  if (brief.isError)
    return (
      <div className="p-6">
        <p role="alert">{brief.error.message}</p>
        <Button onClick={() => void brief.refetch()}>
          Retry project brief
        </Button>
      </div>
    );
  if (proposal && presentation.isPending)
    return (
      <p role="status" className="p-6">
        Loading milestone details…
      </p>
    );
  if (proposal && presentation.isError)
    return (
      <div className="p-6">
        <p role="alert">{presentation.error.message}</p>
        <Button onClick={() => void presentation.refetch()}>
          Retry details
        </Button>
      </div>
    );
  if (proposal && proposal.status !== 'Draft')
    return (
      <div className="p-6">
        <p>
          This proposal is {proposal.status}. The client must request changes
          before it can be revised.
        </p>
        <Link to={'/projects/' + project.projectId}>Back to Project</Link>
      </div>
    );
  return (
    <DraftEditor
      key={selected}
      project={project}
      proposal={proposal}
      command={command}
      initialDetails={presentation.data?.milestones ?? []}
      presentationRevision={presentation.data?.revision ?? 0}
      presentationMatches={presentation.data?.matchesCurrentDefinition ?? false}
      selected={selected}
      onSelected={setSelected}
    />
  );
}
function DraftEditor({
  project,
  proposal,
  command,
  initialDetails,
  presentationRevision,
  presentationMatches,
  selected,
  onSelected,
}: {
  project: ProjectView;
  proposal?: ProposalView;
  command: ReturnType<typeof useProjectCommand>;
  initialDetails: MilestonePresentation[];
  presentationRevision: number;
  presentationMatches: boolean;
  selected: string;
  onSelected: (id: string) => void;
}) {
  const principal = useAuthStore((s) => s.session?.principalId);
  const key =
    'scope-draft:' +
    principal +
    ':' +
    project.projectId +
    ':' +
    (selected || 'new');
  const catalog = useSkillsAndRoles();
  const brief = useProjectBrief(project.projectId);
  const participants = useProjectParticipants(project.projectId);
  const writer = useProposalCommentWriter(
    project.projectId,
    proposal?.proposalId ?? ''
  );
  const cache = useQueryClient();
  const navigate = useNavigate();
  const formId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const running = useRef(false);
  const storedCommand = command.saved?.command;
  const [value, setValue] = useState(() => {
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) return draftSchema.parse(JSON.parse(saved));
      if (proposal) {
        const newKey =
          'scope-draft:' + principal + ':' + project.projectId + ':new';
        const unfinished = sessionStorage.getItem(newKey);
        if (unfinished) {
          const draft = draftSchema.parse(JSON.parse(unfinished));
          if (draft.proposalId === proposal.proposalId) {
            moveDraft(newKey, key, draft);
            return draft;
          }
        }
      }
    } catch {
      /* The server remains authoritative if this local draft is invalid. */
    }
    return {
      definition:
        storedCommand &&
        (storedCommand.kind === 'proposal-create' ||
          storedCommand.kind === 'proposal-update')
          ? storedCommand.payload
          : proposal
            ? definitionOf(proposal)
            : {
                title: project.title,
                description: project.description,
                milestones: [newMilestone(0)],
              },
      details: currentDetails(
        proposal?.milestones.map((m) => m.definition) ?? [],
        initialDetails
      ),
      delivery: proposal?.delivery ??
        brief.data?.brief.delivery ?? { preference: 'WithinOneMonth' },
      comment: '',
      proposalId: proposal?.proposalId,
    };
  });
  const [baseDefinition, setBaseDefinition] = useState(
    proposal ? JSON.stringify(definitionOf(proposal)) : ''
  );
  const [baseRevision, setBaseRevision] = useState(proposal?.revision);
  const [savedPresentation, setSavedPresentation] = useState(
    proposal && presentationMatches
      ? JSON.stringify({
          definition: definitionOf(proposal),
          milestones: currentDetails(
            proposal.milestones.map((m) => m.definition),
            initialDetails
          ),
        })
      : ''
  );
  const [detailRevision, setDetailRevision] = useState(presentationRevision);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(
    () => sessionStorage.getItem(key + ':error') ?? ''
  );
  const [taskEpoch, setTaskEpoch] = useState(0);
  const [taskEdits, setTaskEdits] = useState<Record<number, boolean>>({});
  const [baseDelivery, setBaseDelivery] = useState(
    JSON.stringify(proposal?.delivery ?? null)
  );
  const deliveryDirty = JSON.stringify(value.delivery) !== baseDelivery;
  const definitionDirty = JSON.stringify(value.definition) !== baseDefinition;
  const details = currentDetails(value.definition.milestones, value.details);
  const conflict =
    !!proposal &&
    (definitionDirty || deliveryDirty) &&
    proposal.revision !== baseRevision;
  const locked =
    busy ||
    command.busy ||
    command.pending ||
    command.blocked ||
    writer.busy ||
    writer.blocked ||
    !!writer.pending;
  useEffect(() => {
    if (!definitionDirty && !deliveryDirty) setBaseRevision(proposal?.revision);
  }, [definitionDirty, deliveryDirty, proposal?.revision]);
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      setError(
        'The draft cannot be retained for reload. Keep this page open until saved.'
      );
    }
  }, [key, value]);
  function update(index: number, changes: Partial<PlanningMilestone>) {
    setValue((previous) => ({
      ...previous,
      definition: {
        ...previous.definition,
        milestones: previous.definition.milestones.map((m, i) =>
          i === index ? { ...m, ...changes } : m
        ),
      },
    }));
  }
  async function save(submit: boolean) {
    if (
      running.current ||
      locked ||
      conflict ||
      !formRef.current?.reportValidity()
    )
      return;
    running.current = true;
    setBusy(true);
    setError('');
    let id = value.proposalId;
    let retained = { ...value };
    let completed = false;
    const assertAccount = () => {
      if (useAuthStore.getState().session?.principalId !== principal)
        throw Error('Your account changed. Reload before saving.');
    };
    try {
      sessionStorage.removeItem(key + ':error');
      assertAccount();
      if (submit && Object.values(taskEdits).some(Boolean))
        throw Error(
          'Save your edited tasks before submitting the scope. Empty task storages are allowed.'
        );
      const validated = proposalDefinitionSchema.parse(value.definition);
      const definition = {
        ...validated,
        milestones: validated.milestones.map((m) =>
          plannedMilestoneBudget(
            m,
            catalog.compensationPolicy?.coordinatorPercent
          )
        ),
      };
      retained = { ...retained, definition };
      const delivery = deliveryPreferenceSchema.parse(value.delivery);
      if (submit && delivery.preference === 'SpecificDate') {
        const minimum = minimumDeliveryDate(definition.milestones);
        if (delivery.date < minimum)
          throw Error(
            `Choose an estimated delivery date on or after ${minimum} to cover all milestone weeks.`
          );
      }
      const items = details.map((item) =>
        milestonePresentationSchema.parse(item)
      );
      if (
        definition.milestones.some((m) =>
          m.requirements.some(
            (r) =>
              !catalog.roles.some((role) => role.id === r.roleId) ||
              r.skillIds.some(
                (skill) => !catalog.skills.some((s) => s.id === skill)
              )
          )
        )
      )
        throw Error(
          'A staffing role or skill is unavailable. Review the catalog before saving.'
        );
      const input: ProjectCommand = id
        ? {
            kind: 'proposal-update',
            projectId: project.projectId,
            proposalId: id,
            payload: definition,
          }
        : {
            kind: 'proposal-create',
            projectId: project.projectId,
            payload: definition,
          };
      validateProjectCommand(input);
      if (JSON.stringify(definition) !== baseDefinition || !id) {
        const operation = await command.run(input);
        if (!operation) return;
        id ??= operation.receipt?.createdEntityId ?? undefined;
        if (!id)
          throw Error(
            'The confirmed proposal ID is missing. Check the project before retrying.'
          );
        retained = { ...retained, proposalId: id };
        setValue(retained);
        sessionStorage.setItem(key, JSON.stringify(retained));
        assertAccount();
        setBaseDefinition(JSON.stringify(definition));
      }
      const live = await getProjectView(project.projectId);
      assertAccount();
      let current = live.proposals.find((p) => p.proposalId === id);
      if (
        current?.compensationPolicy &&
        current.compensationPolicy.coordinatorPercent !==
          catalog.compensationPolicy?.coordinatorPercent
      ) {
        await cache.invalidateQueries({ queryKey: ['onboarding', 'catalog'] });
        throw Error(
          'The coordinator percentage changed while saving. Review the refreshed budget before retrying this saved proposal.'
        );
      }
      if (
        !current ||
        current.status !== 'Draft' ||
        JSON.stringify(definitionOf(current)) !== JSON.stringify(definition)
      )
        throw Error(
          'The server scope changed. Reload before saving these descriptions.'
        );
      if (JSON.stringify(current.delivery) !== JSON.stringify(delivery)) {
        if (
          !(await command.run({
            kind: 'proposal-delivery',
            projectId: project.projectId,
            proposalId: id,
            payload: { expectedRevision: current.revision, delivery },
          }))
        )
          return;
        assertAccount();
        current = (await getProjectView(project.projectId)).proposals.find(
          (p) => p.proposalId === id
        );
        if (
          !current ||
          current.status !== 'Draft' ||
          JSON.stringify(current.delivery) !== JSON.stringify(delivery)
        )
          throw Error(
            'The delivery preference changed. Reload before continuing.'
          );
      }
      setBaseDelivery(JSON.stringify(delivery));
      setBaseRevision(current.revision);
      const presentation = JSON.stringify({ definition, milestones: items });
      if (presentation !== savedPresentation || detailRevision === 0) {
        const saved = await putProposalPresentation(project.projectId, id, {
          expectedProposalRevision: current.revision,
          expectedPresentationRevision: detailRevision,
          milestones: items,
        });
        assertAccount();
        setDetailRevision(saved.revision);
        setSavedPresentation(presentation);
        await cache.invalidateQueries({
          queryKey: ['proposal-presentation', principal, project.projectId, id],
        });
      }
      if (submit) {
        assertAccount();
        if (value.comment.trim()) {
          if (!(await writer.send(value.comment, current.revision, id))) {
            sessionStorage.setItem(
              key + ':error',
              'Your response could not be confirmed. Retry the saved response before submitting.'
            );
            return;
          }
          retained = { ...retained, comment: '' };
          setValue(retained);
          sessionStorage.setItem(key, JSON.stringify(retained));
        }
        if (
          await command.run({
            kind: 'proposal-submit',
            projectId: project.projectId,
            proposalId: id,
          })
        ) {
          completed = true;
          sessionStorage.removeItem(key);
          navigate('/projects/' + project.projectId);
          return;
        }
      }
    } catch (reason) {
      const message =
        reason instanceof z.ZodError
          ? reason.issues.map((issue) => issue.message).join('; ')
          : (reason as Error).message;
      setError(message);
      try {
        sessionStorage.setItem(key + ':error', message);
      } catch {
        /* Retain the message in memory. */
      }
    } finally {
      if (
        id &&
        !proposal &&
        !completed &&
        useAuthStore.getState().session?.principalId === principal
      ) {
        try {
          const nextKey =
            'scope-draft:' + principal + ':' + project.projectId + ':' + id;
          moveDraft(key, nextKey, retained);
          onSelected(id);
        } catch {
          setError(
            'The proposal is saved, but its local draft cannot be retained. Keep this page open and retry.'
          );
        }
      }
      running.current = false;
      setBusy(false);
    }
  }
  return (
    <MainScreen
      contentClassName="pageContent scopePageContent"
      backRoute={'/projects/' + project.projectId}
      backTitle={project.title}
      headerContent={
        <div className="buildScopeHeader">
          <div className="headerContent">
            <div className="title">
              <h1>Review the Project Scope and build the milestones</h1>
            </div>
            <div className="scopeActions">
              <Button
                form={formId}
                type="submit"
                data-action="submit"
                disabled={
                  locked ||
                  conflict ||
                  catalog.isLoading ||
                  catalog.isError ||
                  Object.values(taskEdits).some(Boolean)
                }
              >
                Submit Review
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        ref={formRef}
        aria-label="Proposal draft"
        onSubmit={(event) => {
          event.preventDefault();
          void save(
            (event.nativeEvent as SubmitEvent).submitter?.getAttribute(
              'data-action'
            ) === 'submit'
          );
        }}
      />
      <div className="scopeFeedback" aria-live="polite">
        {(error || command.error || writer.error) && (
          <p role="alert">{error || command.error || writer.error}</p>
        )}
        {command.status && <p role="status">{command.status}</p>}
        {command.pending && (
          <Button
            disabled={command.busy || command.blocked}
            onClick={() =>
              void command.run(null).then((result) => {
                if (
                  storedCommand?.kind === 'proposal-create' &&
                  result?.receipt?.createdEntityId
                ) {
                  const id = result.receipt.createdEntityId;
                  const retained = { ...value, proposalId: id };
                  setValue(retained);
                  try {
                    moveDraft(
                      key,
                      'scope-draft:' +
                        principal +
                        ':' +
                        project.projectId +
                        ':' +
                        id,
                      retained
                    );
                    onSelected(id);
                  } catch {
                    setError(
                      'The proposal is saved. Keep this page open to preserve the local draft.'
                    );
                  }
                }
                if (
                  result &&
                  storedCommand &&
                  (storedCommand.kind === 'task-create' ||
                    storedCommand.kind === 'task-update')
                ) {
                  const milestone = proposal?.milestones.find(
                    (m) =>
                      m.taskStorage.taskStorageId === storedCommand.storageId
                  );
                  if (milestone) {
                    const taskKey =
                      key +
                      ':tasks:' +
                      milestone.definition.key +
                      ':' +
                      (storedCommand.kind === 'task-create'
                        ? 'new'
                        : storedCommand.taskId);
                    sessionStorage.removeItem(taskKey);
                    sessionStorage.removeItem(taskKey + ':source');
                    setTaskEdits((previous) => ({
                      ...previous,
                      [milestone.definition.key]: false,
                    }));
                    setTaskEpoch((previous) => previous + 1);
                  }
                }
              })
            }
          >
            Check pending request
          </Button>
        )}
        {writer.pending && (
          <Button
            disabled={writer.busy}
            onClick={() =>
              void writer.send().then((saved) => {
                if (saved)
                  setValue((previous) => ({ ...previous, comment: '' }));
              })
            }
          >
            Retry response
          </Button>
        )}
        {(writer.pending || writer.blocked) && (
          <Button disabled={writer.busy} onClick={writer.discard}>
            Discard local response
          </Button>
        )}
        {conflict && (
          <p role="alert">
            The server draft changed. Reload its saved definition before editing
            further.
          </p>
        )}
        {catalog.isError && (
          <Button onClick={() => void catalog.rolesQuery.refetch()}>
            Retry catalog
          </Button>
        )}
      </div>
      <fieldset
        disabled={locked || conflict}
        className="buildScopeContent min-w-0 border-0"
      >
        <div className="infoCol min-w-0">
          {(brief.isError || participants.isError) && (
            <div role="alert">
              <p>Could not load the project brief or client details.</p>
              <Button
                variant="outline"
                onClick={() => {
                  void brief.refetch();
                  void participants.refetch();
                }}
              >
                Retry project details
              </Button>
            </div>
          )}
          <ProjectDetailsCard
            project={project}
            brief={brief.data?.brief}
            participants={participants.data}
          />
          <MilestonesFormCard
            coordinatorPercent={catalog.compensationPolicy?.coordinatorPercent}
            delivery={value.delivery}
            onDelivery={(delivery) =>
              setValue((previous) => ({ ...previous, delivery }))
            }
            definitions={value.definition.milestones}
            details={details}
            formId={formId}
            onChange={update}
            onDetails={(items) =>
              setValue((previous) => ({ ...previous, details: items }))
            }
            addMilestone={() =>
              setValue((previous) => ({
                ...previous,
                definition: {
                  ...previous.definition,
                  milestones: [
                    ...previous.definition.milestones,
                    newMilestone(
                      Math.max(
                        -1,
                        ...previous.definition.milestones.map((m) => m.key)
                      ) + 1
                    ),
                  ],
                },
              }))
            }
            removeMilestone={(index) => {
              const saved = proposal?.milestones.find(
                (m) =>
                  m.definition.key === value.definition.milestones[index]!.key
              );
              if (
                !saved?.taskStorage.tasks.length ||
                window.confirm(
                  'Removing this milestone when saved will also remove its attached tasks. Continue?'
                )
              )
                setValue((previous) => ({
                  ...previous,
                  definition: {
                    ...previous.definition,
                    milestones: previous.definition.milestones.filter(
                      (_, i) => i !== index
                    ),
                  },
                }));
            }}
            renderTasks={(milestone) => (
              <PlanningTaskFields
                key={milestone.key + ':' + taskEpoch}
                draftKey={key + ':tasks:' + milestone.key}
                milestone={proposal?.milestones.find(
                  (m) => m.definition.key === milestone.key
                )}
                projectId={project.projectId}
                proposalRevision={proposal?.revision}
                command={command}
                disabled={locked || definitionDirty}
                onDirty={(dirty) =>
                  setTaskEdits((previous) =>
                    previous[milestone.key] === dirty
                      ? previous
                      : { ...previous, [milestone.key]: dirty }
                  )
                }
              />
            )}
            footer={
              <div className="flex flex-wrap gap-4">
                <Button
                  type="submit"
                  form={formId}
                  disabled={catalog.isLoading || catalog.isError}
                >
                  Save draft
                </Button>
              </div>
            }
          />
        </div>
        <div className="reviewCol min-w-0">
          <ReviewPanelCard
            coordinatorPercent={catalog.compensationPolicy?.coordinatorPercent}
            definitions={value.definition.milestones}
            details={details}
            brief={brief.data?.brief}
            coordinator={participants.data?.coordinator}
            comment={value.comment}
            onComment={(comment) =>
              setValue((previous) => ({ ...previous, comment }))
            }
          />
          <div className="helpSection">
            <div className="infoRow">
              <div className="iconWrapper">
                <i className="ri-question-line" />
              </div>
              <div className="textTitles">
                <h3>Have some doubts about the project brief?</h3>
                <p>
                  Discuss adjustments with the client in the project review.
                </p>
              </div>
            </div>
            <Link
              className="text-primary underline"
              to={'/projects/' + project.projectId}
            >
              Open scope responses
            </Link>
          </div>
        </div>
      </fieldset>
      {project.proposals.length > 1 && (
        <label className="scopeProposalPicker">
          Proposal
          <select
            disabled={locked}
            value={selected}
            onChange={(event) => {
              if (
                window.confirm(
                  'Switch proposals? The current draft is retained locally.'
                )
              )
                onSelected(event.target.value);
            }}
          >
            <option value="">New draft</option>
            {project.proposals.map((p) => (
              <option key={p.proposalId} value={p.proposalId}>
                {p.title} · {p.status}
              </option>
            ))}
          </select>
        </label>
      )}
    </MainScreen>
  );
}
